import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Typography,
  SvgIconProps
} from '@mui/material';

export interface Column<T> {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any, row: T) => React.ReactNode;
}

interface DataGridProps<T> {
  columns: Column<T>[];
  rows: T[];
  totalCount?: number;
  keyField: keyof T;
  loading?: boolean;
  page: number;
  rowsPerPage: number;
  emptyMessage?: string;
  emptyIcon?: React.ReactElement<SvgIconProps>;
  emptySubMessage?: string;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowsPerPageOptions?: number[];
  stickyHeader?: boolean;
  maxHeight?: string | number;
}

/**
 * A reusable data grid component for displaying tabular data
 */
function DataGrid<T>({
  columns,
  rows,
  totalCount,
  keyField,
  loading = false,
  page,
  rowsPerPage,
  emptyMessage = 'No data found',
  emptyIcon,
  emptySubMessage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  stickyHeader = true,
  maxHeight = 'calc(100vh - 300px)',
}: DataGridProps<T>) {
  const count = totalCount !== undefined ? totalCount : rows.length;
  const colSpan = columns.length;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper 
        sx={{ 
          width: '100%', 
          overflow: 'hidden',
          boxShadow: 1,
          borderRadius: 1,
        }}
      >
        {loading && (
          <LinearProgress sx={{ height: 2 }} />
        )}
        
        <TableContainer sx={{ maxHeight }}>
          <Table stickyHeader={stickyHeader} size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ 
                      minWidth: column.minWidth,
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <LinearProgress sx={{ width: '50%', maxWidth: '200px' }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading data...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      {emptyIcon && React.cloneElement(emptyIcon, { 
                        sx: { 
                          fontSize: 48,
                          color: 'text.disabled',
                          mb: 1
                        }
                      })}
                      <Typography variant="body1" fontWeight="medium" color="text.primary">
                        {emptyMessage}
                      </Typography>
                      {emptySubMessage && (
                        <Typography variant="body2" color="text.secondary">
                          {emptySubMessage}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow 
                    hover 
                    tabIndex={-1} 
                    key={String(row[keyField])}
                  >
                    {columns.map((column) => {
                      const value = row[column.id as keyof T];
                      return (
                        <TableCell key={column.id} align={column.align || 'left'}>
                          {column.format ? column.format(value, row) : String(value ?? '')}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </Paper>
    </Box>
  );
}

export default DataGrid;
